import type { ReportRow } from '@/api/keywordMetrics';
import {
  EMPTY_RAW_METRICS,
  accumulateInto,
  campaignLookupKey,
  rowInCampaignScope,
  rowToRaw,
  totalsFromMetricRows,
  type MetricPolarity,
  type RawMetricTotals,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';
import {
  deriveTargetingTypeMetrics,
  type TargetingTypeDerivedMetrics,
} from '@/utils/workspaceTargetingTypeMetrics';

export const PLACEMENT_IDS = [
  'top_of_search',
  'product_pages',
  'rest_of_search',
  'homepage',
] as const;

export type PlacementId = (typeof PLACEMENT_IDS)[number];

export const PLACEMENT_ROWS: Array<{ id: PlacementId; label: string }> = [
  { id: 'top_of_search', label: 'Top of Search' },
  { id: 'product_pages', label: 'Product Pages' },
  { id: 'rest_of_search', label: 'Rest of Search' },
  { id: 'homepage', label: 'Homepage on-Amazon' },
];

export type PlacementDerivedMetrics = TargetingTypeDerivedMetrics;

export const EMPTY_PLACEMENT_METRICS: PlacementDerivedMetrics = {
  ...EMPTY_RAW_METRICS,
  ctr: 0,
  cpc: 0,
  cr: 0,
  acos: 0,
  roas: 0,
  asp: 0,
};

export type PlacementMetricColumnDef = {
  key: keyof PlacementDerivedMetrics;
  label: string;
  polarity: MetricPolarity;
  format: WorkspaceMetricColumnDef['format'];
};

export const PLACEMENT_METRIC_COLUMNS: PlacementMetricColumnDef[] = [
  { key: 'cost', label: 'Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'purchases30d', label: 'Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impressions', polarity: 'higher_is_better', format: 'int' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
  { key: 'roas', label: 'ROAS', polarity: 'higher_is_better', format: 'ratio' },
  { key: 'asp', label: 'ASP', polarity: 'higher_is_better', format: 'currency' },
];

export const derivePlacementMetrics = deriveTargetingTypeMetrics;

export type PlacementReportRow = ReportRow & {
  placement?: string;
};

function normalizePlacementToken(value: unknown): string {
  if (value == null) return '';
  return String(value)
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, '_');
}

/**
 * Classify a placement token into exactly one UI bucket.
 * Priority: Homepage → Top of Search → Product Pages → Rest of Search.
 */
export function classifyPlacement(placement: unknown): PlacementId | null {
  const token = normalizePlacementToken(placement);
  if (!token) return null;

  if (
    token === 'HOME' ||
    token === 'HOMEPAGE' ||
    token.includes('HOMEPAGE') ||
    token.includes('HOME_ON') ||
    (token.startsWith('HOME') && !token.includes('SEARCH'))
  ) {
    return 'homepage';
  }

  if (
    token === 'TOP_OF_SEARCH' ||
    token === 'PLACEMENT_TOP' ||
    token.includes('TOP_OF_SEARCH') ||
    token.includes('TOPOFSEARCH')
  ) {
    return 'top_of_search';
  }

  if (
    token === 'DETAIL_PAGE' ||
    token === 'PRODUCT_PAGE' ||
    token === 'PLACEMENT_PRODUCT_PAGE' ||
    token.includes('DETAIL_PAGE') ||
    token.includes('PRODUCT_PAGE') ||
    token.includes('DETAIL')
  ) {
    return 'product_pages';
  }

  if (
    token === 'OTHER' ||
    token === 'OTHER_ON_SEARCH' ||
    token === 'REST_OF_SEARCH' ||
    token === 'PLACEMENT_REST_OF_SEARCH' ||
    token.includes('REST_OF_SEARCH') ||
    token.includes('OTHER_ON') ||
    token.includes('OTHER')
  ) {
    return 'rest_of_search';
  }

  return null;
}

function finalizePlacementMap(
  map: Map<string, RawMetricTotals>
): Map<PlacementId, PlacementDerivedMetrics> {
  const out = new Map<PlacementId, PlacementDerivedMetrics>();
  for (const id of PLACEMENT_IDS) {
    const raw = map.get(id) ?? { ...EMPTY_RAW_METRICS };
    out.set(id, derivePlacementMetrics(raw));
  }
  return out;
}

export function aggregatePlacementMetrics(
  rows: PlacementReportRow[],
  scopedCampaignLookupKeys: Set<string> | null
): Map<PlacementId, PlacementDerivedMetrics> {
  const rawByPlacement = new Map<string, RawMetricTotals>();
  for (const id of PLACEMENT_IDS) {
    rawByPlacement.set(id, { ...EMPTY_RAW_METRICS });
  }

  for (const row of rows) {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    if (!campaignId || !rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys)) {
      continue;
    }
    const bucket = classifyPlacement(row.placement);
    if (!bucket) continue;
    accumulateInto(rawByPlacement, bucket, rowToRaw(row));
  }

  return finalizePlacementMap(rawByPlacement);
}

/** Per-campaign placement metrics keyed by `campaignLookupKey(adProduct, campaignId)`. */
export function aggregatePlacementMetricsByCampaign(
  rows: PlacementReportRow[]
): Map<string, Map<PlacementId, PlacementDerivedMetrics>> {
  const rawByCampaign = new Map<string, Map<string, RawMetricTotals>>();

  for (const row of rows) {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    if (!campaignId) continue;
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    const bucket = classifyPlacement(row.placement);
    if (!bucket) continue;

    const lookup = campaignLookupKey(adProduct, campaignId);
    let placementRaw = rawByCampaign.get(lookup);
    if (!placementRaw) {
      placementRaw = new Map<string, RawMetricTotals>();
      for (const id of PLACEMENT_IDS) {
        placementRaw.set(id, { ...EMPTY_RAW_METRICS });
      }
      rawByCampaign.set(lookup, placementRaw);
    }
    accumulateInto(placementRaw, bucket, rowToRaw(row));
  }

  const out = new Map<string, Map<PlacementId, PlacementDerivedMetrics>>();
  for (const [lookup, placementRaw] of rawByCampaign) {
    out.set(lookup, finalizePlacementMap(placementRaw));
  }
  return out;
}

export type PlacementSummaryPayload = {
  buckets?: Array<{
    id: string;
    label: string;
    current: PlacementDerivedMetrics;
    previous: PlacementDerivedMetrics | null;
  }>;
  unclassified?: {
    current?: Partial<PlacementDerivedMetrics> | null;
    previous?: Partial<PlacementDerivedMetrics> | null;
  };
  totals?: {
    current?: PlacementDerivedMetrics | null;
    previous?: PlacementDerivedMetrics | null;
  };
};

function coercePlacementMetrics(
  value: Partial<PlacementDerivedMetrics> | null | undefined
): PlacementDerivedMetrics {
  if (!value) return { ...EMPTY_PLACEMENT_METRICS };
  return derivePlacementMetrics({
    impressions: Number(value.impressions) || 0,
    clicks: Number(value.clicks) || 0,
    cost: Number(value.cost) || 0,
    purchases30d: Number(value.purchases30d) || 0,
    sales30d: Number(value.sales30d) || 0,
  });
}

/** Map backend placement summary payload into Workspace table maps. */
export function placementMapsFromSummary(
  data: PlacementSummaryPayload | null | undefined
): {
  current: Map<PlacementId, PlacementDerivedMetrics>;
  previous: Map<PlacementId, PlacementDerivedMetrics>;
  currentTotals: PlacementDerivedMetrics;
  previousTotals: PlacementDerivedMetrics;
} {
  const current = new Map<PlacementId, PlacementDerivedMetrics>();
  const previous = new Map<PlacementId, PlacementDerivedMetrics>();
  for (const id of PLACEMENT_IDS) {
    current.set(id, { ...EMPTY_PLACEMENT_METRICS });
    previous.set(id, { ...EMPTY_PLACEMENT_METRICS });
  }
  for (const bucket of data?.buckets ?? []) {
    if (!PLACEMENT_IDS.includes(bucket.id as PlacementId)) continue;
    const id = bucket.id as PlacementId;
    current.set(id, coercePlacementMetrics(bucket.current));
    previous.set(id, coercePlacementMetrics(bucket.previous));
  }
  // Footer Total must equal the four visible buckets. API `totals` historically
  // mixed in unclassified / blank campaign-grain placement rows.
  const currentTotals = totalsFromMetricRows([...current.values()]);
  const previousTotals = totalsFromMetricRows([...previous.values()]);
  return { current, previous, currentTotals, previousTotals };
}
